/* eslint-disable no-shadow */
import React, {
  useContext, useEffect, useRef, useState, useMemo
} from 'react'
import { useHistory } from 'react-router-dom'
import PlansSection from '@apps/flows/activation/sections/PlansSection'
import ConsumerActivationPaths from '@sites/consumer/activation/ConsumerActivationPaths'
import DealerActivationPaths from '@sites/dealer/activation/DealerActivationPaths'
import { useLocation } from 'react-router'
import Sites from '@sites/index'
import { setPageAnalytics } from '@apps/utils/Analytics'
import {
  APPLICATION_STATE,
  ApplicationContext
} from '@apps/contexts/ApplicationContext'
import { useSelector } from 'react-redux'
import { PLANS_STATE } from '@apps/redux/features/PlansSlice'
import { getById, getBySlug } from '@apps/utils/contentful'
import { looksLikeUuid, resolveGuidFromCatalog } from '@apps/utils/planMfeUtils'
import { isSafeInternalPath } from '@apps/utils/navigation'
import { ANALYTICS_EVENT_TYPES } from '@/apps/utils/constants'
import useTagManagerInfo from '../../_engine/hooks/useTagManagerInfo'

const resolveGuidFromPlans = (plans, planId) => {
  if (!Array.isArray(plans) || planId == null) return undefined
  const selectedId = String(planId)
  const match = plans.find((candidate) => String(candidate?.id) === selectedId)
  if (!match) return undefined

  const candidates = [match?.pricePlanGuid, match?.guid]
  const uuidGuid = candidates.find((guid) => looksLikeUuid(guid))
  if (uuidGuid) return uuidGuid

  return candidates.find(Boolean)
}

const PlansPage = () => {
  const {
    userType,
    user: { accountInfo, selectedLanguage, email },
    quote: { quoteInfo },
    content: { pageContent, featureFlags: pageFeatureFlags }
  } = useSelector((state) => state.common)
  const {
    [PLANS_STATE.PLAN]: plan,
    [PLANS_STATE.ALL_PLANS]: allPlans,
    localizedPlanItem
  } = useSelector((state) => state.plansPage)

  const consumerActivationPaths = new ConsumerActivationPaths()
  const dealerActivationPaths = new DealerActivationPaths()
  const routes = userType === Sites.dealer ? dealerActivationPaths : consumerActivationPaths

  const history = useHistory()
  const featureFlags = pageFeatureFlags?.[0] || {}

  useEffect(() => {
    if (featureFlags?.showSplashing && history) {
      history.push(routes.Splash)
    }
  }, [featureFlags?.showSplashing, history, userType])

  const isDealer = userType === Sites.dealer
  const pagePrefix = isDealer ? 'Dealer' : 'Consumer'
  const content = useMemo(() => {
    const plansPage = getById(pageContent, `plansPage${pagePrefix}`) || {}
    const { shortTextFields } = plansPage[selectedLanguage] || {}

    const isFr = selectedLanguage === 'fr'
    return {
      phoneNumber: getBySlug(shortTextFields, 'phoneNumberSectionTitle')?.value,
      step: getBySlug(shortTextFields, 'step')?.value,
      goBackToActivation: getBySlug(shortTextFields, 'goBackToPrevStep')?.value
        || getBySlug(shortTextFields, 'goBackToActivation')?.value
        || (isFr ? "Retour à l'étape précédente" : 'Go Back to Previous Step')
    }
  }, [pageContent, selectedLanguage])

  const { step, phoneNumber, goBackToActivation } = content

  const previousStepText = step && phoneNumber ? `${step} 3: ${phoneNumber}` : goBackToActivation

  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language,
    [APPLICATION_STATE.CURRENT_REGION]: region,
    [APPLICATION_STATE.IDENTITY]: identity,
    [APPLICATION_STATE.SERVICES]: { setPatchQuote }
  } = useContext(ApplicationContext)

  const location = useLocation()
  const tagManagerInfo = useTagManagerInfo({
    plan_name: plan?.sluggedName || 'NA',
    plan_cost: plan?.value || 'NA',
    customer_ban: 'NA',
    customer_quote_id: accountInfo?.quoteId || quoteInfo?.quoteId || 'NA',
    dealer_info: userType === Sites.dealer ? identity : 'NA'
  })

  const pushTagManagerToDataLayer = () => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(setPageAnalytics(location, 'form', region, language, tagManagerInfo))
  }

  const pushErrorAnalytics = (clickText, error) => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(
      setPageAnalytics(
        location,
        'click',
        region,
        language,
        {
          ...tagManagerInfo,
          click_text: clickText,
          field_warning: error?.message || 'NA',
          error_response_code: error?.code || error?.response?.status || 'NA'
        },
        clickText,
        ANALYTICS_EVENT_TYPES.CLICK
      )
    )
  }

  // eslint-disable-next-line camelcase
  const pushClickAnalytics = (click_text, event_type = ANALYTICS_EVENT_TYPES.CLICK, event_click = ANALYTICS_EVENT_TYPES.CLICK) => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(
      setPageAnalytics(
        location,
        'click',
        region,
        language,
        {
          ...tagManagerInfo,
          // eslint-disable-next-line camelcase
          click_text
        },
        event_type,
        event_click
      )
    )
  }

  useEffect(() => {
    if (userType === Sites.dealer && identity) {
      setTimeout(() => {
        pushTagManagerToDataLayer()
      }, 0)
    }
  }, [region, language, identity])

  useEffect(() => {
    if (userType === Sites.consumer) {
      setTimeout(() => {
        pushTagManagerToDataLayer()
      }, 0)
    }
  }, [region, language])

  useEffect(() => {
    const unblock = history.block((location, action) => {
      if (action === 'pop' || action === 'POP') {
        return false
      }

      return () => {
        unblock()
      }
    })
  }, [history])

  const [showPlanError, setShowPlanError] = useState(false)
  const planErrorRef = useRef(null)
  const fromPath = isSafeInternalPath(location?.state?.from) ? location.state.from : undefined

  const backLinkProps = {
    to: fromPath || routes.ChooseNumber,
    label: goBackToActivation,
    onClick: () => pushClickAnalytics(previousStepText, previousStepText)
  }

  const updateQuote = async (selectedPlan) => {
    const catalogGuid = resolveGuidFromCatalog(localizedPlanItem, language, selectedPlan?.id)
    const allPlansGuid = resolveGuidFromPlans(allPlans, selectedPlan?.id)
    const rawGuid = selectedPlan?.pricePlanGuid ?? selectedPlan?.guid
    const resolvedGuid =
      [catalogGuid, allPlansGuid, rawGuid].find((guid) => looksLikeUuid(guid))
      || catalogGuid
      || allPlansGuid
      || rawGuid

    // pricePlanGuid is sent as-is, matching setCreateQuote. Both getPlanById and
    // the MFE BFF return `idx-*` guids, so gating this on a UUID shape dropped it
    // from every patchQuote while createQuote sent the same value and was accepted.
    const input = {
      email,
      pricePlanId: selectedPlan?.id,
      pricePlanGuid: resolvedGuid
    }

    const response = await setPatchQuote(input)

    if (!response?.data?.accountId) {
      throw new Error('Patch quote succeeded without accountId in response data')
    }

    return response
  }

  const handleAddToCart = async (selectedPlan) => {
    const targetPlan = selectedPlan || plan

    if (targetPlan?.id && email) {
      try {
        await updateQuote(targetPlan)
      } catch (err) {
        pushErrorAnalytics('Plan update quote failed', err)
        setShowPlanError(true)
        planErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
    }

    // When changing from a consumer plan detail URL, return to the newly selected
    // plan detail URL so IntroPage refreshes the current-order summary from the slug.
    const planRoute = routes.Plan
    const escapedPlanPath = planRoute?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const planDetailsPathPattern = escapedPlanPath
      ? new RegExp(`^${escapedPlanPath.replace(':planName', '[^/]+')}/?$`)
      : undefined
    const selectedPlanPath = planRoute && targetPlan?.sluggedName
      ? planRoute.replace(':planName', targetPlan.sluggedName)
      : undefined

    if (fromPath && selectedPlanPath && planDetailsPathPattern?.test(fromPath)) {
      history.push(selectedPlanPath)
      return
    }

    if (fromPath) {
      history.push(fromPath)
      return
    }

    history.push(routes.Payment)
  }

  // Early return to prevent flash of content or downstream effects (analytics, data fetches)
  // before the redirect completes. Placed after all hooks so hook order stays stable, and
  // gated on featureFlags being confirmed loaded so we don't blank the page on first paint
  // while the flags are still arriving asynchronously.
  const featureFlagsLoaded = Array.isArray(pageFeatureFlags) && pageFeatureFlags.length > 0
  if (featureFlagsLoaded && featureFlags?.showSplashing) {
    return null
  }

  return (
    <div className="flex flex-col w-full">
      <PlansSection
        planErrorRef={planErrorRef}
        showPlanError={showPlanError}
        setShowPlanError={setShowPlanError}
        pushClickAnalytics={pushClickAnalytics}
        backLink={backLinkProps}
        onAddToCart={handleAddToCart}
      />
    </div>
  )
}

export default PlansPage
